import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface CounterProps {
    /** Final value as it should read, for example "10 000", "4,5" or "400+". */
    value: string
    className?: string
    duration?: number
}

const parse = (value: string): { number: number, decimals: number, prefix: string, suffix: string, grouped: boolean } | null => {
    const match = value.match(/^([^\d]*)([\d\s.,]+?)([^\d]*)$/)

    if (!match) return null

    const [, prefix, raw, suffix] = match
    const grouped = /\d\s\d/.test(raw)
    const normalized = raw.replace(/\s/g, '').replace(',', '.')
    const number = Number(normalized)

    if (!Number.isFinite(number)) return null

    const decimals = normalized.includes('.') ? normalized.split('.')[1].length : 0

    return { number, decimals, prefix, suffix, grouped }
}

const format = (n: number, decimals: number, grouped: boolean): string => {
    const fixed = n.toFixed(decimals)
    const [int, dec] = fixed.split('.')
    const intPart = grouped ? int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : int

    return dec ? `${intPart},${dec}` : intPart
}

/** Renders the final value on the server and counts up from zero when scrolled into view. */
export const Counter: FC<CounterProps> = ({ value, className, duration = 1600 }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const el = ref.current
        const parsed = parse(value)

        if (!el || !parsed) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const rect = el.getBoundingClientRect()

        if (rect.top < window.innerHeight && rect.bottom > 0) return

        const { number, decimals, prefix, suffix, grouped } = parsed
        let frame = 0

        el.textContent = `${prefix}${format(0, decimals, grouped)}${suffix}`

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return

            observer.disconnect()

            const started = performance.now()

            const step = (now: number): void => {
                const t = Math.min(1, (now - started) / duration)
                const eased = 1 - Math.pow(1 - t, 4)

                el.textContent = `${prefix}${format(number * eased, decimals, grouped)}${suffix}`

                if (t < 1) frame = window.requestAnimationFrame(step)
            }

            frame = window.requestAnimationFrame(step)
        }, { rootMargin: '0px 0px -15% 0px' })

        observer.observe(el)

        return () => {
            observer.disconnect()
            window.cancelAnimationFrame(frame)
            el.textContent = value
        }
    }, [value, duration])

    return (
        <span ref={ref} className={cn('tabular', className)}>
            {value}
        </span>
    )
}
